import { Autocomplete, Chip, CircularProgress, TextField } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { listPartners, type PartnerRow } from '../../services/partnerUserService';

interface PartnerMultiSelectProps {
  value: PartnerRow[];
  onChange: (rows: PartnerRow[]) => void;
  entityType: string;
  serviceHub?: string;
  label?: string;
  placeholder?: string;
  size?: 'small' | 'medium';
  /** When set, overrides the default partners list (e.g. drivers from completed/cancelled rides). */
  optionsOverride?: PartnerRow[];
  noOptionsText?: string;
}

function partnerLabel(p: PartnerRow) {
  const name = `${p.firstName || ''} ${p.lastName || ''}`.trim();
  return [name || 'Partner', p.phone].filter(Boolean).join(' · ');
}

export default function PartnerMultiSelect({
  value,
  onChange,
  entityType,
  serviceHub,
  label = 'Partners',
  placeholder = 'Select one or more',
  size = 'small',
  optionsOverride,
  noOptionsText = 'No data available',
}: PartnerMultiSelectProps) {
  const { data, isLoading } = useQuery({
    queryKey: ['partners-select', entityType, serviceHub],
    queryFn: () => listPartners({
      entityType,
      serviceHub: serviceHub || undefined,
      page: 1,
      limit: 500,
    }),
    enabled: optionsOverride === undefined,
  });
  const options = optionsOverride ?? data?.data ?? [];

  return (
    <Autocomplete
      multiple
      size={size}
      options={options}
      value={value}
      onChange={(_, rows) => onChange(rows)}
      loading={optionsOverride === undefined && isLoading}
      noOptionsText={noOptionsText}
      getOptionLabel={partnerLabel}
      isOptionEqualToValue={(a, b) => a._id === b._id}
      filterSelectedOptions
      renderTags={(rows, getTagProps) =>
        rows.map((row, index) => {
          const { key: _tagKey, ...tagProps } = getTagProps({ index });
          return <Chip key={row._id} size="small" label={partnerLabel(row)} {...tagProps} />;
        })
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={value.length ? '' : placeholder}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {isLoading ? <CircularProgress color="inherit" size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}
